/**
 * Per-operation duplicate prevention tracker.
 *
 * Tracks which specific operations are currently in progress using unique keys
 * (e.g., "delete-node-abc123", "remove-link-src-tgt"). If the same operation is
 * attempted while already running, it is rejected. Different operations are NOT
 * blocked — only the exact same operationKey is prevented from running concurrently.
 *
 * Used by executeTreeEdit in DecisionTreeEdit to prevent duplicate API calls
 * when users click quickly (e.g., double-clicking "Delete Node" on the same node).
 *
 * Drag/position saves, layout toggles, and create-orphan-node are intentionally
 * NOT tracked here — they either use debounced batching (drag) or each invocation
 * is a genuinely new operation (create node).
 */
export class OperationDedupTracker {
    private inProgress: Set<string> = new Set();

    /**
     * Attempt to acquire the operation lock.
     * @returns true if the operation can proceed (not a duplicate), false if already in progress.
     */
    tryAcquire(operationKey: string): boolean {
        if (this.inProgress.has(operationKey)) {
            return false;
        }
        this.inProgress.add(operationKey);
        return true;
    }

    /**
     * Release the operation lock. Should be called in a finally block.
     */
    release(operationKey: string): void {
        this.inProgress.delete(operationKey);
    }

    /**
     * Check if an operation is currently in progress.
     */
    isInProgress(operationKey: string): boolean {
        return this.inProgress.has(operationKey);
    }

    /**
     * Get the number of operations currently in progress.
     */
    get activeCount(): number {
        return this.inProgress.size;
    }

    /**
     * Clear all tracked operations (useful for cleanup/testing).
     */
    clear(): void {
        this.inProgress.clear();
    }
}
